import { matchCommand } from "../../commands/parse.js";
import { readEnv } from "../../utils/env.js";
import { isRecord, parseJsonValue } from "../../utils/file-guards.js";
import * as log from "../../log.js";

export type { LoginCredentialKind, OAuthService, ParsedLoginCommand } from "./types.js";
import type { OAuthService, ParsedLoginCommand } from "./types.js";

const DEFAULT_GOOGLE_WORKSPACE_CLI_SCOPES = [
  "https://www.googleapis.com/auth/drive",
  "https://mail.google.com/",
  "https://www.googleapis.com/auth/calendar",
  "https://www.googleapis.com/auth/spreadsheets",
  "https://www.googleapis.com/auth/documents",
  "https://www.googleapis.com/auth/chat.messages.create",
];

const DEFAULT_GOOGLE_CLOUD_SDK_SCOPES = [
  "openid",
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/cloud-platform",
];

// Conservative default: enough for `gh` CLI repo/user/org operations, but
// without `workflow` (can dispatch CI), `write:packages` (can publish
// packages), or `project`. Operators who need those can opt in via
// GITHUB_OAUTH_SCOPES (or MIKAN_GITHUB_OAUTH_SCOPES) to keep the blast radius
// of a compromised agent host explicit and configurable.
const DEFAULT_GITHUB_OAUTH_SCOPES = ["repo", "read:user", "user:email", "read:org", "gist"];

function resolveScopesFromEnv(envKey: string, fallback: string[]): string[] {
  const raw = readEnv(envKey);
  if (!raw) return fallback;

  const scopes = raw
    .split(/[\s,]+/)
    .map((scope) => scope.trim())
    .filter(Boolean);

  return scopes.length > 0 ? scopes : fallback;
}

function getBuiltinOAuthServices(): OAuthService[] {
  return [
    {
      id: "github",
      label: "GitHub",
      aliases: ["github", "github_oauth", "gh_oauth"],
      authorizationUrl: "https://github.com/login/oauth/authorize",
      tokenUrl: "https://github.com/login/oauth/access_token",
      scopes: resolveScopesFromEnv("GITHUB_OAUTH_SCOPES", DEFAULT_GITHUB_OAUTH_SCOPES),
      clientIdEnvKey: "GITHUB_OAUTH_CLIENT_ID",
      clientSecretEnvKey: "GITHUB_OAUTH_CLIENT_SECRET",
      accessTokenEnvKeys: ["GITHUB_OAUTH_ACCESS_TOKEN", "GH_TOKEN"],
      refreshTokenEnvKey: "GITHUB_OAUTH_REFRESH_TOKEN",
    },
    {
      id: "google_workspace_cli",
      label: "Google Workspace CLI",
      aliases: ["google_workspace_cli", "gws", "googleworkspace", "google-workspace-cli"],
      authorizationUrl: "https://accounts.google.com/o/oauth2/v2/auth",
      tokenUrl: "https://oauth2.googleapis.com/token",
      scopes: resolveScopesFromEnv(
        "GOOGLE_WORKSPACE_CLI_OAUTH_SCOPES",
        DEFAULT_GOOGLE_WORKSPACE_CLI_SCOPES,
      ),
      clientIdEnvKey: "GOOGLE_WORKSPACE_CLI_CLIENT_ID",
      clientSecretEnvKey: "GOOGLE_WORKSPACE_CLI_CLIENT_SECRET",
      authorizationParams: {
        access_type: "offline",
        include_granted_scopes: "true",
        prompt: "consent",
      },
      fileOutput: {
        type: "authorized_user",
        relativePath: "gws.json",
        targetPath: "/root/.config/gws/credentials.json",
      },
    },
    {
      id: "google_cloud_sdk",
      label: "Google Cloud SDK (gcloud)",
      aliases: ["google_cloud_sdk", "gcloud", "google-cloud-sdk", "google_cloud", "gcp"],
      authorizationUrl: "https://accounts.google.com/o/oauth2/v2/auth",
      tokenUrl: "https://oauth2.googleapis.com/token",
      scopes: resolveScopesFromEnv(
        "GOOGLE_CLOUD_SDK_OAUTH_SCOPES",
        DEFAULT_GOOGLE_CLOUD_SDK_SCOPES,
      ),
      clientIdEnvKey: "GOOGLE_CLOUD_SDK_CLIENT_ID",
      clientSecretEnvKey: "GOOGLE_CLOUD_SDK_CLIENT_SECRET",
      authorizationParams: {
        access_type: "offline",
        include_granted_scopes: "true",
        prompt: "consent",
      },
      fileOutput: {
        type: "authorized_user",
        relativePath: "gcloud-adc.json",
        targetPath: "/root/.config/gcloud/application_default_credentials.json",
        envKey: "GOOGLE_APPLICATION_CREDENTIALS",
        additionalEnvKeys: ["CLOUDSDK_AUTH_CREDENTIAL_FILE_OVERRIDE"],
      },
    },
  ];
}

function parseOAuthService(value: unknown, index: number): OAuthService | null {
  if (!isRecord(value)) {
    log.logWarning(`Skipping OAUTH_SERVICES_JSON[${index}]: expected an object`);
    return null;
  }

  const id = typeof value.id === "string" ? value.id.trim() : "";
  const label = typeof value.label === "string" ? value.label.trim() : "";
  const authorizationUrl =
    typeof value.authorizationUrl === "string" ? value.authorizationUrl.trim() : "";
  const tokenUrl = typeof value.tokenUrl === "string" ? value.tokenUrl.trim() : "";
  const clientIdEnvKey =
    typeof value.clientIdEnvKey === "string" ? value.clientIdEnvKey.trim() : "";
  const clientSecretEnvKey =
    typeof value.clientSecretEnvKey === "string" ? value.clientSecretEnvKey.trim() : "";
  const missing = [
    ["id", id],
    ["label", label],
    ["authorizationUrl", authorizationUrl],
    ["tokenUrl", tokenUrl],
    ["clientIdEnvKey", clientIdEnvKey],
    ["clientSecretEnvKey", clientSecretEnvKey],
  ]
    .filter((entry) => !entry[1])
    .map((entry) => entry[0]);

  if (missing.length > 0) {
    const labelForLog = id ? ` (${id})` : "";
    log.logWarning(
      `Skipping OAUTH_SERVICES_JSON[${index}]${labelForLog}: missing ${missing.join(", ")}`,
    );
    return null;
  }

  const aliases = parseStringArray(value.aliases, "aliases", index, id);
  if (aliases === null) return null;
  const scopes = parseStringArray(value.scopes, "scopes", index, id);
  if (scopes === null) return null;
  const authorizationParams = parseStringRecord(
    value.authorizationParams,
    "authorizationParams",
    index,
    id,
  );
  if (authorizationParams === null) return null;

  const accessTokenEnvKeys: string[] = [];
  if (typeof value.accessTokenEnvKey === "string" && value.accessTokenEnvKey.trim()) {
    accessTokenEnvKeys.push(value.accessTokenEnvKey.trim());
  }
  for (const field of ["additionalAccessTokenEnvKeys", "accessTokenEnvKeys"] as const) {
    const keys = parseStringArray(value[field], field, index, id);
    if (keys === null) return null;
    for (const key of keys ?? []) {
      const trimmed = key.trim();
      if (trimmed && !accessTokenEnvKeys.includes(trimmed)) accessTokenEnvKeys.push(trimmed);
    }
  }

  let fileOutput: OAuthService["fileOutput"];
  if (isRecord(value.fileOutput)) {
    const fileOutputObj = value.fileOutput;
    const type = typeof fileOutputObj.type === "string" ? fileOutputObj.type.trim() : "";
    const relativePath =
      typeof fileOutputObj.relativePath === "string" ? fileOutputObj.relativePath.trim() : "";
    const targetPath =
      typeof fileOutputObj.targetPath === "string" ? fileOutputObj.targetPath.trim() : undefined;
    const envKey =
      typeof fileOutputObj.envKey === "string" ? fileOutputObj.envKey.trim() : undefined;
    const additionalEnvKeys = parseStringArray(
      fileOutputObj.additionalEnvKeys,
      "fileOutput.additionalEnvKeys",
      index,
      id,
    );
    if (additionalEnvKeys === null) return null;
    if (type === "authorized_user" && relativePath) {
      fileOutput = {
        type: "authorized_user",
        relativePath,
        targetPath,
        envKey,
        additionalEnvKeys,
      };
    }
  }

  return {
    id: id.toLowerCase(),
    label,
    aliases: aliases?.map((v) => v.toLowerCase()) ?? [id.toLowerCase()],
    authorizationUrl,
    tokenUrl,
    scopes: scopes ?? [],
    clientIdEnvKey,
    clientSecretEnvKey,
    accessTokenEnvKeys: accessTokenEnvKeys.length > 0 ? accessTokenEnvKeys : undefined,
    refreshTokenEnvKey:
      typeof value.refreshTokenEnvKey === "string" ? value.refreshTokenEnvKey.trim() : undefined,
    authorizationParams: authorizationParams ?? undefined,
    fileOutput,
  };
}

function parseStringArray(
  value: unknown,
  field: string,
  index: number,
  id: string,
): string[] | undefined | null {
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.some((entry) => typeof entry !== "string")) {
    log.logWarning(`Skipping OAUTH_SERVICES_JSON[${index}] (${id}): ${field} must be strings`);
    return null;
  }
  return value;
}

function parseStringRecord(
  value: unknown,
  field: string,
  index: number,
  id: string,
): Record<string, string> | undefined | null {
  if (value === undefined) return undefined;
  if (!isRecord(value) || Object.values(value).some((entry) => typeof entry !== "string")) {
    log.logWarning(`Skipping OAUTH_SERVICES_JSON[${index}] (${id}): ${field} must be strings`);
    return null;
  }
  return value as Record<string, string>;
}

export function getOAuthServices(): OAuthService[] {
  const raw = readEnv("OAUTH_SERVICES_JSON");
  const builtins = getBuiltinOAuthServices();
  if (!raw) return builtins;

  let parsed: unknown[];
  try {
    parsed = parseJsonValue(raw, Array.isArray, (detail) =>
      detail === "unexpected JSON shape"
        ? "expected a JSON array of OAuth service definitions"
        : detail,
    );
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    log.logWarning(
      detail === "expected a JSON array of OAuth service definitions"
        ? "Ignoring OAUTH_SERVICES_JSON: expected a JSON array of OAuth service definitions"
        : "Ignoring OAUTH_SERVICES_JSON: invalid JSON",
      detail,
    );
    return builtins;
  }

  const custom = parsed
    .map((serviceValue, index) => parseOAuthService(serviceValue, index))
    .filter((service): service is OAuthService => service !== null);
  const byId = new Map<string, OAuthService>();
  for (const service of builtins) byId.set(service.id, service);
  for (const service of custom) byId.set(service.id, service);
  return [...byId.values()];
}

export function resolveOAuthService(input: string): OAuthService | undefined {
  const normalized = input.trim().toLowerCase();
  if (!normalized) return undefined;
  return getOAuthServices().find(
    (service) => service.id === normalized || service.aliases.includes(normalized),
  );
}

const LOGIN_COMMANDS = ["/login", "/pi-login"] as const;

export function parseLoginCommand(text: string): ParsedLoginCommand | null {
  const matched = matchCommand(text, LOGIN_COMMANDS);
  if (!matched) return null;

  const [subcommand, operation, name, ...extra] = matched.args;

  if (!subcommand) return { action: "setup" };

  if (subcommand.toLowerCase() === "shared") {
    const op = operation?.toLowerCase();
    if (op === "list" && !name && extra.length === 0) {
      return { action: "shared_list" };
    }
    if ((op === "create" || op === "update" || op === "delete") && !!name && extra.length === 0) {
      return {
        action: `shared_${op}` as "shared_create" | "shared_update" | "shared_delete",
        name,
      };
    }
    return null;
  }

  if (subcommand.toLowerCase() === "copy" && operation && !name && extra.length === 0) {
    return { action: "copy_shared", name: operation };
  }

  // Backward-compatible: older `/pi-login gh` / `/pi-login gws` forms opened the
  // generic login page and let the portal handle provider choice.
  if (!operation && extra.length === 0) return { action: "setup" };

  return null;
}
